import { returnsSettingsTabPath } from "@redotech/redo-merchant-app-common/settings/returns-settings-parent-path";
import { ReturnsSettingsTab } from "@redotech/redo-merchant-app-common/settings/returns-settings-tab";
import type { Location } from "react-router-dom";

/** A query param rather than router state, because editors rewrite their query with `replace`, which drops state. */
const RETURN_TO_PARAM = "returnTo";

/** Where a flow editor's Close goes; only in-app paths, so the param can't redirect off-site. */
export function flowEditorReturnTo(search: string): string | undefined {
  const returnTo = new URLSearchParams(search).get(RETURN_TO_PARAM);
  return returnTo?.startsWith("/") && !returnTo.startsWith("//")
    ? returnTo
    : undefined;
}

function withReturnTo(to: string, returnTo: string | undefined): string {
  return returnTo
    ? `${to}?${new URLSearchParams({ [RETURN_TO_PARAM]: returnTo })}`
    : to;
}

/** Link into a flow editor whose Close comes back to the current page. */
export function flowEditorLinkFrom(to: string, location: Location): string {
  return withReturnTo(to, location.pathname + location.search);
}

/** Link to a sibling flow editor that keeps the current editor's Close destination. */
export function siblingFlowEditorLink(to: string, location: Location): string {
  return withReturnTo(to, flowEditorReturnTo(location.search));
}

const SETTINGS_PREFIX = "/settings/";

/** Old pages the editors close to, and the tab a merchant on the new page expects instead. */
const NEW_UI_CLOSE_TAB: Readonly<Record<string, ReturnsSettingsTab>> = {
  "customer-portal": ReturnsSettingsTab.POLICIES,
  shipping: ReturnsSettingsTab.LABELS,
  warehouse: ReturnsSettingsTab.WAREHOUSE,
};

/** Where Close goes without a returnTo: the editor's old page, or its tab for a merchant on the new page. */
export function flowEditorClosePath(
  closeNavigationPath: string,
  useNewUi: boolean,
): string {
  if (!useNewUi || !closeNavigationPath.startsWith(SETTINGS_PREFIX)) {
    return closeNavigationPath;
  }
  const tab =
    NEW_UI_CLOSE_TAB[closeNavigationPath.slice(SETTINGS_PREFIX.length)];
  return tab
    ? `${SETTINGS_PREFIX}${returnsSettingsTabPath(tab)}`
    : closeNavigationPath;
}
